/**
 * Widget G: Client Age Distribution
 * 
 * Displays a vertical bar chart showing the distribution of clients across
 * age buckets: Under 40, 40-49, 50-59, 60-69, 70+.
 */

import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Card, CardContent } from "../../ui/card";
import { ClientType } from "../../../types";
import { calculateAgeDistribution, AgeDistributionData } from "../utils/widgetCalculations";
import { UNIFIED_CHART_COLORS, ACCENT_COLOR, getUnifiedChartColor } from "./widgetColors";
import { ChartTooltip, SIMPLE_BAR_MARGINS, AXIS_STYLE, GRID_STYLE, CHART_ANIMATION } from "./chartUtils";
import XanoAPI from "../../../lib/XanoAPI";

interface AgeDistributionWidgetProps {
  clients: ClientType[];
  isLoading?: boolean;
  userId?: number; // Optional user ID filter (singular int)
}

const AGE_BUCKETS = [
  { key: "under40", label: "Under 40", color: UNIFIED_CHART_COLORS[1] },  // Sky blue
  { key: "40to49", label: "40-49", color: UNIFIED_CHART_COLORS[0] },     // Teal
  { key: "50to59", label: "50-59", color: UNIFIED_CHART_COLORS[6] },      // Mint
  { key: "60to69", label: "60-69", color: UNIFIED_CHART_COLORS[3] },      // Peach
  { key: "70plus", label: "70+", color: UNIFIED_CHART_COLORS[2] },       // Lavender
];

export function AgeDistributionWidget({
  clients,
  isLoading = false,
  userId,
}: AgeDistributionWidgetProps) {
  const api = XanoAPI.getInstance();

  const { data: apiData, isLoading: apiLoading } = useQuery<{
    buckets: {
      under40: number;
      "40to49": number;
      "50to59": number;
      "60to69": number;
      "70plus": number;
    };
  } | null>({
    queryKey: ["client-age-distribution", userId],
    queryFn: async (): Promise<{
      buckets: {
        under40: number;
        "40to49": number;
        "50to59": number;
        "60to69": number;
        "70plus": number;
      };
    } | null> => {
      const result = await api.getClientAgeDistribution(userId);
      console.log("🔍 AgeDistributionWidget: API response:", result);
      console.log("🔍 AgeDistributionWidget: API response type:", typeof result);
      console.log("🔍 AgeDistributionWidget: Has buckets?", !!result?.buckets);
      if (!result) {
        console.log("⚠️ AgeDistributionWidget: Endpoint returned null");
      }
      return result;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const data = useMemo(() => {
    // Only use API data - no sample data fallback
    let result: {
      buckets: {
        under40: number;
        "40to49": number;
        "50to59": number;
        "60to69": number;
        "70plus": number;
      };
    };
    
    if (apiData && apiData.buckets) {
      // API data is available and valid - use it
      result = apiData;
    } else {
      // No data available - return empty structure
      result = {
        buckets: {
          under40: 0,
          "40to49": 0,
          "50to59": 0,
          "60to69": 0,
          "70plus": 0,
        },
      };
    }
    
    // Transform to chart data format
    const totalClients = Object.values(result.buckets).reduce((a, b) => a + b, 0);
    
    const chartData = AGE_BUCKETS.map((bucket) => {
      const count = result.buckets[bucket.key as keyof typeof result.buckets];
      return {
        ageRange: bucket.label,
        count,
        percentage: totalClients > 0 ? (count / totalClients) * 100 : 0,
        fill: bucket.color,
        key: bucket.key,
      };
    });

    return { chartData, totalClients };
  }, [apiData]);

  const isLoadingData = isLoading || apiLoading;

  if (isLoadingData) {
    return (
      <Card>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground text-sm">Loading...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.totalClients === 0) {
    return (
      <Card>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground">
              No age data available for the selected clients.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }


  const chartDataWithTotal = data.chartData.map(item => ({
    ...item,
    totalClients: data.totalClients,
  }));

  return (
    <Card>
      <CardContent>
        <div className="h-[340px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartDataWithTotal} margin={SIMPLE_BAR_MARGINS}>
              <CartesianGrid {...GRID_STYLE} vertical={false} />
              <XAxis 
                dataKey="ageRange" 
                tick={AXIS_STYLE.tick}
                tickLine={false}
              />
              <YAxis 
                label={{ 
                  value: "Number of Clients", 
                  angle: -90, 
                  position: "insideLeft",
                  style: AXIS_STYLE.label
                }}
                allowDecimals={false}
                tick={AXIS_STYLE.tick}
                tickLine={false}
                width={25}
              />
              <Tooltip 
                content={<ChartTooltip 
                  formatter={(value, name) => {
                    const dataPoint = chartDataWithTotal.find(d => d.count === value);
                    const percentage = data.totalClients > 0
                      ? ((value / data.totalClients) * 100).toFixed(1)
                      : "0.0";
                    return [`${value} clients (${percentage}%)`, dataPoint?.ageRange || name];
                  }}
                />} 
                cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
              />
              <Bar 
                dataKey="count" 
                radius={[8, 8, 0, 0]}
                {...CHART_ANIMATION}
              >
                {chartDataWithTotal.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 pt-3 border-t text-center">
          {data.chartData.map((item) => {
            const percentage = data.totalClients > 0
              ? ((item.count / data.totalClients) * 100).toFixed(1)
              : "0.0";
            return (
              <div key={item.key} className="space-y-1">
                <div className="text-lg font-bold" style={{ color: item.fill }}>
                  {item.count}
                </div>
                <div className="text-xs font-medium text-foreground">{item.ageRange}</div>
                <div className="text-xs text-muted-foreground">{percentage}%</div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

